import { ref, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import PaywallService from '@/services/PaywallService'

/**
 * The Paywall section's Stripe Connect flow, reusable from a site page:
 * `POST /paywall/init` hands back a Stripe onboarding link (or nothing when
 * the account is already active), we open it in a new tab and poll
 * `GET /paywall/activationStatus` until Stripe reports `active`. The user
 * never leaves the current page, so "return to the site page" is the tab
 * they are already on. Unlink = `DELETE /paywall` (castr pauses the paid
 * plans of every site on that account).
 *
 * @param {{ onConnected?: () => void|Promise<void>, onUnlinked?: () => void|Promise<void> }} [hooks]
 */
export function useStripeConnect(hooks = {}) {
	const store = useStore()
	const connecting = ref(false)
	const oauthLink = ref(null)
	const failed = ref(false)
	const unlinking = ref(false)
	const error = ref(null)
	let poller = null

	const stopPolling = () => {
		if (poller) clearInterval(poller)
		poller = null
	}

	const finishConnected = async () => {
		stopPolling()
		connecting.value = false
		oauthLink.value = null
		failed.value = false
		store.dispatch('User/setPaywallLinked', true)
		if (hooks.onConnected) await hooks.onConnected()
	}

	const poll = () => {
		stopPolling()
		poller = setInterval(async () => {
			try {
				const res = await PaywallService.stripeLinkingStatus()
				if (res.status === 'active') await finishConnected()
				if (res.status === 'failed') {
					stopPolling()
					connecting.value = false
					failed.value = true
				}
			} catch (err) {
				stopPolling()
				connecting.value = false
				error.value = err.message || "Couldn't check the Stripe link status."
			}
		}, 3000)
	}

	/** Start (or resume) onboarding. Opens Stripe in a new tab and polls. */
	const connect = async () => {
		error.value = null
		failed.value = false
		try {
			const master = await PaywallService.initPaywall()
			if (master?.oauthLink) {
				oauthLink.value = master.oauthLink
				connecting.value = true
				poll()
				window.open(master.oauthLink, '_blank', 'noopener')
				return
			}
			// No onboarding link = the account is already active (e.g. relinking).
			await finishConnected()
		} catch (err) {
			error.value = err.message || "Couldn't start the Stripe connection."
		}
	}

	/** Re-open the onboarding tab if the user closed it. */
	const reopen = () => {
		if (oauthLink.value) window.open(oauthLink.value, '_blank', 'noopener')
	}

	const cancel = () => {
		stopPolling()
		connecting.value = false
		oauthLink.value = null
	}

	/** Soft unlink: keeps the paywall account, pauses paid plans on all sites. */
	const unlink = async () => {
		unlinking.value = true
		error.value = null
		try {
			const res = await PaywallService.unlinkPaywall()
			await store.dispatch('User/unloadPaywall')
			await store.dispatch('Paywalls/unloadPaywall')
			if (hooks.onUnlinked) await hooks.onUnlinked(res)
			return res
		} catch (err) {
			error.value = err.message || "Couldn't unlink Stripe."
			throw err
		} finally {
			unlinking.value = false
		}
	}

	onBeforeUnmount(stopPolling)

	return { connecting, oauthLink, failed, unlinking, error, connect, reopen, cancel, unlink }
}
